import { useEffect } from 'react';
import { shouldAnimate } from '../utils/motion';

/**
 * Reveals every [data-reveal] element inside rootRef as it enters the viewport.
 * Only elements that start below the fold are hidden, so the first screen
 * (and the prerendered HTML) is always complete.
 */
export default function useScrollReveal(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !shouldAnimate() || !('IntersectionObserver' in window)) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' },
    );

    const elements = Array.from(root.querySelectorAll('[data-reveal]'));
    elements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      // Skip items parked off to the side (e.g. later cards of the mobile carousel).
      if (rect.top > window.innerHeight && rect.left < window.innerWidth) {
        element.classList.add('is-pending-reveal');
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
      elements.forEach((element) => element.classList.remove('is-pending-reveal', 'is-revealed'));
    };
  }, [rootRef]);
}
